// components/user/lead/calculator/NavigationButtons.jsx

import React from "react";
import { Button } from "antd";
import { 
  LeftOutlined, 
  RightOutlined, 
  LoadingOutlined 
} from "@ant-design/icons";

const NavigationButtons = ({
  currentStep,
  isLastStep,
  onPrev,
  onNext,
  onSubmit,
  submitting,
  isDarkMode,
  canProceed,
  isUpgradeStep,
  isAgeStep,
  upgradeOptions,
  selectedUpgrades,
  stepCategories,
  hasSelectedFaultsInStep = false,
}) => {
  // Get current categories based on step
  let faultStepIndex;
  const hasUpgrades = upgradeOptions && upgradeOptions.length > 0;
  
  if (hasUpgrades) {
    faultStepIndex = currentStep - 2;
  } else {
    faultStepIndex = currentStep - 1;
  }

  const currentCategories = faultStepIndex >= 0 && faultStepIndex < stepCategories.length
    ? stepCategories[faultStepIndex] || []
    : [];

  // Check if current step has any faults at all
  const hasAnyFaultsInStep = currentCategories.some(cat => 
    cat.faults && cat.faults.length > 0
  );

  // Check if this is a fault step
  const isFaultStep = faultStepIndex >= 0 && faultStepIndex < stepCategories.length;

  // Get the label_if_no_fault from the first category in the step
  const getNoFaultLabel = () => {
    if (!isFaultStep || !hasAnyFaultsInStep) return 'Excellent';
    
    // Find the first category that has faults and has a label_if_no_fault
    for (const category of currentCategories) {
      if (category.faults && category.faults.length > 0 && category.label_if_no_fault) {
        return category.label_if_no_fault;
      }
    }
    return 'Excellent';
  };

  const noFaultLabel = getNoFaultLabel();

  const getButtonText = () => {
    if (isUpgradeStep) return "Continue to Age";
    if (isAgeStep) return "Continue to Condition";
    
    // Check if it's a fault step
    if (isFaultStep && hasAnyFaultsInStep && !isLastStep) {
      // If faults are selected, show "Continue to Calculate"
      if (hasSelectedFaultsInStep) {
        return "Continue to Calculate";
      }
      // If no faults selected, show "{label} & Continue"
      return `${noFaultLabel} & Continue`;
    }
    
    return "Continue";
  };

  const getSubmitText = () => {
    return submitting ? "Calculating..." : "Get Exact Value";
  };

  const hasAllUpgradesSelected = () => {
    if (!upgradeOptions || upgradeOptions.length === 0) return true;
    for (const group of upgradeOptions) {
      const hasSelection = group.options?.some(opt => selectedUpgrades?.includes(opt.id));
      if (!hasSelection) return false;
    }
    return true;
  };

  const isNextDisabled = () => {
    if (isUpgradeStep) return !hasAllUpgradesSelected();
    if (isAgeStep) return !canProceed;
    return false;
  };

  // Show "Next" on fault step if not last step
  const showNextOnFaultStep = isFaultStep && hasAnyFaultsInStep && !isLastStep;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 flex items-center gap-2 px-3 py-2.5 border-t ${
        isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      } backdrop-blur-md md:bg-transparent md:static md:border-0 md:p-0`}
      style={{ zIndex: 51 }}
    >
      {!isLastStep || showNextOnFaultStep ? (
        <Button
          onClick={onNext}
          disabled={isNextDisabled()}
          icon={<RightOutlined />}
          iconPosition="end"
          className={`h-10 px-6 rounded-lg text-sm font-medium !text-white border-0 flex-1 ${
            isNextDisabled() 
              ? '!bg-gray-300 cursor-not-allowed dark:!bg-gray-600 hover:!bg-gray-300 dark:hover:!bg-gray-600' 
              : '!bg-green-500 hover:!bg-green-600'
          }`}
        >
          {getButtonText()}
        </Button>
      ) : (
        <Button
          onClick={onSubmit}
          disabled={submitting}
          icon={submitting ? <LoadingOutlined spin /> : <RightOutlined />}
          iconPosition="end"
          className={`h-10 px-6 rounded-lg text-sm font-medium !text-white border-0 flex-1 ${
            submitting
              ? '!bg-gray-300 cursor-not-allowed dark:!bg-gray-600 hover:!bg-gray-300 dark:hover:!bg-gray-600'
              : '!bg-green-500 hover:!bg-green-600'
          }`}
        >
          {getSubmitText()}
        </Button>
      )}
    </div>
  );
};

export default NavigationButtons;